/**
 * Notification API Service
 * REQ-SOP-6.2: Notification Center Frontend API
 */

import api from '../api';

export interface Notification {
  id: number;
  user_id: number;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  link?: string;
  is_read: boolean;
  created_at: string;
  read_at?: string;
}

export interface NotificationResponse {
  success: boolean;
  data: Notification[];
  unreadCount: number;
}

class NotificationAPI {
  /**
   * Get unread notifications
   */
  async getUnread(limit: number = 10): Promise<NotificationResponse> {
    try {
      const response = await api.get('/api/notifications/unread', {
        params: { limit }
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to get unread notifications:', error);
      throw new Error(error.response?.data?.details || 'Failed to get notifications');
    }
  }

  /**
   * Get recent notifications (both read and unread)
   */
  async getRecent(limit: number = 50): Promise<NotificationResponse> {
    try {
      const response = await api.get('/api/notifications/recent', {
        params: { limit }
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to get recent notifications:', error);
      throw new Error(error.response?.data?.details || 'Failed to get notifications');
    }
  }

  /**
   * Get unread notification count only
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await api.get('/api/notifications/count');
      return response.data.count;
    } catch (error: any) {
      console.error('Failed to get notification count:', error);
      return 0; // Return 0 on error instead of throwing
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: number): Promise<void> {
    try {
      await api.post(`/api/notifications/${notificationId}/mark-read`);
    } catch (error: any) {
      console.error('Failed to mark notification as read:', error);
      throw new Error(error.response?.data?.details || 'Failed to mark as read');
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    try {
      await api.post('/api/notifications/mark-all-read');
    } catch (error: any) {
      console.error('Failed to mark all notifications as read:', error);
      throw new Error(error.response?.data?.details || 'Failed to mark all as read');
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: number): Promise<void> {
    try {
      await api.delete(`/api/notifications/${notificationId}`);
    } catch (error: any) {
      console.error('Failed to delete notification:', error);
      throw new Error(error.response?.data?.details || 'Failed to delete notification');
    }
  }
}

export const notificationAPI = new NotificationAPI();
export default notificationAPI;
